import { http } from '../http';

/**
 * Acompanhamento de Oficina (3ª entrega da at-53, pedido do Victor de 08/09/2026).
 *
 * Os veículos do consultor que estão na oficina, e os entregues há pouco. Substituiu o que antes
 * vivia espalhado pela carteira — que voltou a ser só sobre a carteira.
 *
 * ⚠️ O escopo é do TOKEN: a rota devolve a carteira do consultor logado e não aceita parâmetro de
 * consultor. Não há como pedir a de outro.
 */

/** Uma resposta dentro da thread — plana, sempre pendurada na raiz. */
export interface RespostaDaOficina {
  id: string;
  texto: string;
  autorNome: string;
  criadoEm: string | null;
}

/**
 * Um comentário que a oficina registrou sobre o veículo.
 *
 * 🛑 **DESLIGADO desde a at-66 (10/09/2026, decisão de Victor):** *"os comentários do SOAC não devem
 * aparecer no SAAC, pelo menos não agora. Aquilo são informações muito operacionais para os
 * consultores"*. Hoje o backend devolve sempre `comentarios: []` e `totalDeComentarios: 0` — o corte
 * é **na resposta da API**, não na tela, porque o pedido é sobre acesso e o que chega ao navegador
 * é alcançável pelo DevTools.
 *
 * 📌 **Estes tipos e os componentes que os renderizam ficam de propósito**, cobertos por teste: o
 * espelho do backend continua guardando os registros, e religar é trocar
 * `COMENTARIOS_VISIVEIS_PARA_O_CONSULTOR` para `true` em `oficina-espelho.service.ts` — a tela volta
 * a exibi-los sem nenhuma outra alteração.
 *
 * 🔴 `deAtendimentoAnterior` **precisa** ser renderizado quando voltarem. Na oficina, comentário de
 * escopo "placa" atravessa atendimentos: o veículo que já esteve lá antes traz junto o que foi dito
 * naquela vez. Sem a marca, um comentário de meses atrás é lido como sendo desta entrada — e o
 * consultor o repassa ao associado como se fosse de agora.
 */
export interface ComentarioDaOficina {
  id: string;
  texto: string;
  autorNome: string;
  criadoEm: string | null;
  /** Contexto pronto, vindo da oficina: "Placa", o nome da etapa, ou "Sub-tarefa: X". */
  escopoLabel: string;
  deAtendimentoAnterior: boolean;
  respostas: RespostaDaOficina[];
}

export interface VeiculoNaOficina {
  placa: string;
  modelo: string | null;
  associado: string | null;
  /**
   * Consultor dono do veículo (at-87). Só vem na visão de TI/ADMIN, que vê todos os veículos;
   * para o consultor é `null` (é sempre ele).
   */
  consultor: string | null;
  esteiraNome: string;
  diasNaOficina: number;
  /** ⚠️ Prazo TOTAL da etapa, NÃO os dias restantes — todo texto tem de dizer isso. */
  previsaoDiasNaEtapa: number | null;
  entradaEm: string | null;
  etapaDesde: string | null;
  encerradoEm: string | null;
  sincronizadoEm: string;
  comentarios: ComentarioDaOficina[];
  /** Total na oficina. Maior que `comentarios.length` ⇒ a lista veio cortada. */
  totalDeComentarios: number;
}

/**
 * Como foi a tentativa de falar com a oficina **nesta** abertura da tela.
 *
 * 🔴 `EM_ANDAMENTO` e `FALHOU` **não podem ser tratados igual**: o primeiro é o cold start do Cloud
 * Run (a oficina hiberna sem uso e leva segundos para acordar) e termina sozinho — a tela reconsulta
 * e mostra; o segundo não vem, e quem lê precisa saber que o dado na tela é velho.
 */
export type EstadoDaConsultaDeOficina =
  | 'ATUALIZADA'
  | 'EM_ANDAMENTO'
  | 'FALHOU'
  | 'NAO_CONFIGURADA';

export interface ConsultaDeOficina {
  estado: EstadoDaConsultaDeOficina;
  /** Texto PRONTO do backend — a tela não reescreve regra (convenção at-24/at-32). */
  mensagem: string;
}

export interface AcompanhamentoDeOficina {
  /** `false` = integração desligada neste ambiente. A tela diz isso em vez de "nenhum veículo". */
  configurado: boolean;
  naOficina: VeiculoNaOficina[];
  entregues: VeiculoNaOficina[];
  /** Quando o espelho foi lido pela última vez. `null` = nenhum veículo esteve na oficina ainda. */
  consultadoEm: string | null;
  /**
   * 🔴 O desfecho da tentativa desta abertura. Sem ele o botão "Atualizar" pode não fazer nada e a
   * tela seguir exibindo a data da consulta anterior — que se lê como "atualizei agora e é isso".
   * Defeito real, achado no teste do Victor em 08/09/2026 com o SOAC fora do ar.
   */
  consulta: ConsultaDeOficina;
}

/**
 * ⚠️ **Esta LANÇA**, ao contrário de `fetchNovidades`.
 *
 * Aqui a informação da oficina é o conteúdo da tela inteira, não um enfeite: falhar em silêncio
 * mostraria "nenhum veículo na oficina" para quem tem veículo lá — e o consultor diria isso ao
 * associado. Quem chama trata e mostra o erro.
 */
export const fetchAcompanhamentoDeOficina = async (): Promise<AcompanhamentoDeOficina> => {
  const { data } = await http.get<AcompanhamentoDeOficina>('/oficina-espelho/meus-veiculos');
  return data;
};
